import { biz } from "./config";
import { useCms } from "./useCms";
import { resolveMediaUrl } from "./media";

/**
 * useSiteSettings
 * -----------------
 * Returns a `biz`-shaped object (same fields Header/Footer/Contact already
 * use) sourced from Admin > Website Settings, falling back field-by-field
 * to the static lib/config.js values for anything not yet set in the CMS
 * (or while the API is still loading/unreachable).
 *
 * logoUrl/logoLightUrl/footerLogoUrl/faviconUrl default to null (not the
 * static /logo.png) so callers can tell "nothing uploaded yet" apart from
 * "an admin explicitly wants this logo" and fall back to their own local
 * asset themselves - see Header.jsx/Footer.jsx.
 *
 * googleRating/googleReviewCount/googleReviewsUrl/googleBusinessProfileUrl/
 * googleMapsUrl/customersServed default to "" / null (never a guessed
 * number) - callers must treat an empty value as "not configured yet, hide
 * this claim" rather than substituting a fallback figure of their own.
 */
const EMPTY_GOOGLE_FIELDS = {
  shortBrandName: "", businessDescription: "", serviceRegion: "",
  officeAddress: "", officeLabel: "Registered Office", googleMapsUrl: "",
  googleBusinessProfileUrl: "", googleReviewsUrl: "", googleRating: null, googleReviewCount: null,
  customersServed: "",
};

export function useSiteSettings() {
  const { data } = useCms("/api/settings", null);
  if (!data) {
    return {
      ...biz, ...EMPTY_GOOGLE_FIELDS,
      logoUrl: null, logoLightUrl: null, footerLogoUrl: null, faviconUrl: null,
    };
  }

  return {
    ...biz,
    name: data.businessName || biz.name,
    shortBrandName: data.shortBrandName || "",
    businessDescription: data.businessDescription || "",
    serviceRegion: data.serviceRegion || "",
    phone: data.phone || biz.phone,
    email: data.email || biz.email,
    address: data.address || biz.address,
    officeAddress: data.officeAddress || "",
    officeLabel: data.officeLabel || "Registered Office",
    googleMapsUrl: data.googleMapsUrl || "",
    googleBusinessProfileUrl: data.googleBusinessProfileUrl || "",
    googleReviewsUrl: data.googleReviewsUrl || "",
    googleRating: data.googleRating ?? null,
    googleReviewCount: data.googleReviewCount ?? null,
    customersServed: data.customersServed || "",
    hours: data.businessHours || biz.hours,
    socials: {
      facebook: data.socials?.facebook || biz.socials.facebook,
      instagram: data.socials?.instagram || biz.socials.instagram,
      tiktok: data.socials?.tiktok || biz.socials.tiktok,
      linkedin: data.socials?.linkedin || biz.socials.linkedin,
      youtube: data.socials?.youtube || biz.socials.youtube || "",
    },
    logoUrl: resolveMediaUrl(data.logoUrl) || null,
    logoLightUrl: resolveMediaUrl(data.logoLightUrl) || null,
    footerLogoUrl: resolveMediaUrl(data.footerLogoUrl) || null,
    faviconUrl: resolveMediaUrl(data.faviconUrl) || null,
  };
}
